import React, { useContext, useEffect, useState } from "react";
import * as OneDrive from "../actions/oneDrive";
import moment from "moment";

import { Button, Select, Checkbox, Input, Field, Spinner } from "@fluentui/react-components";
import { YearDropdown } from "../components/YearDropdown";
import CustomLoader from "../components/CustomLoader";
import { REPORT_TYPES, ReportType } from "../types/types";
import { AppContext } from "../providers/ContextProvider";
import { ApiContext, IFetchHoursBody, IProjectCompnayID } from "../providers/ApiProvider";
import { IProject } from "../types/types";
import { AlertContext } from "../providers/AlertProvider";
import ErrorMessage from "../components/ErrorMessage";

export default function ReportsTab() {
    const { context, user, token, versionNumber } = useContext(AppContext);

    const { errorMsg, setErrorMsg } = useContext(AlertContext);
    const { getServerSideToken, fetchHours, fetchYearly, getCompanyByName, fetchHasHours, getUsers, getProjects } = useContext(ApiContext);
    const [projectsList, setProjectsList] = useState<string[]>([]);
    const [userList, setUserList] = useState<string[]>([]);
    const [projectCompanyID, setProjectCompanyID] = useState<IProjectCompnayID[]>([]);
    const [currentCompanyProjects, setCurrentCompanyProjects] = useState<IProject[]>([]);
    const [usersData, setUsersData] = useState<any[]>([]);
    const [companiesList, setCompaniesList] = useState<string[] | undefined>();
    const [isDisabled, setIsDisabled] = useState<boolean>(true);
    const [isCompanyDisabled, setIsCompanyDisabled] = useState<boolean>(true);
    const [startDate, setStartDate] = useState<number>();
    const [endDate, setEndDate] = useState<number>();
    const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
    const [selectedProjectsName, setSelectedProjectsName] = useState<string[]>([]);
    const [selectedUsersNames, setSelectedUsersNames] = useState<string[]>([]);
    const [selectedUsersIDs, setSelectedUsersIDs] = useState<string[]>([]);
    const [selectedCompany, setSelectedCompany] = useState<string>();
    const [selectedCompanyName, setSelectedCompanyName] = useState<string>();
    const [companySelected, setCompanySelected] = useState<boolean>(false);
    const [excludeDefaultProject, setExcludeDefaultProject] = useState<boolean>(false);
    const [popup, setPopup] = useState<any>(null);
    const [serverSideToken, setServerSideToken] = useState<string>();
    const [reportType, setReportType] = useState<string>();
    const [year, setYear] = useState<number>(new Date().getFullYear());
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false);

    useEffect(() => {
        // Wait for required data to be available
        if (!token || !user || !context) {
            return;
        }

        // If already initialized, don't run again
        if (isInitialized) {
            return;
        }

        // Check if user has tenant ID
        if (!context.user?.tenant?.id) {
            setIsLoading(false);
            return;
        }

        // Check admin permission
        if (!user.is_admin) {
            setErrorMsg("You don't have permission to access this page. If you think this is a mistake, please contact Toni K.");
            setIsLoading(false);
            setIsInitialized(true); // Mark as initialized to prevent re-running
            return;
        }

        // Initialize and fetch data
        setIsInitialized(true);
        setIsLoading(true);

        // Make API calls in parallel for better performance
        Promise.all([getServerSideToken(token, context.user?.tenant?.id), _getUsers(token), getCompanies(token)])
            .then(([serverToken]) => {
                if (serverToken) {
                    setServerSideToken(serverToken);
                }
                setIsLoading(false);
            })
            .catch((error) => {
                console.error(error);
                setErrorMsg("Error fetching data");
                setIsLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, user, context, isInitialized]);

    const _getUsers = async (token: string) => {
        try {
            const listOfUserNames: string[] = [];
            const users = await getUsers(token);
            users.forEach((user) => {
                listOfUserNames.push(user.full_name);
            });

            if (listOfUserNames === undefined || listOfUserNames.length == 0) {
                console.warn("No Users found");
            }

            setUserList(listOfUserNames);
            setUsersData(users);
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    // eslint-disable-next-line no-unused-vars
    const createReportWorkBook = async (serversideToken: string, data: BodyInit, type: string | undefined) => {
        try {
            const date = new Date();
            const year = date.getFullYear();
            const timestamp = moment(date).format("YYYYMMDD-HHmmss");
            if (!user) {
                throw "User not found!";
            }
            const folderName = `Time Tracker/${user.full_name}/${year}`;
            let filename = "";
            let yearlyString = "";

            switch (type) {
                case ReportType.TIME_ENTRIES:
                    break;
                case ReportType.YEAR_REPORT:
                    yearlyString = "YEARLY_REPORT_";
                    break;
                default:
                    break;
            }

            if (selectedProjectsName.length > 0) {
                if (selectedUsersNames.length > 0) {
                    filename = `${selectedCompanyName}_${selectedProjectsName}_${selectedUsersNames}_${timestamp}.xlsx`;
                } else {
                    filename = `${selectedCompanyName}_${selectedProjectsName}_${timestamp}.xlsx`;
                }
            } else {
                if (selectedUsersNames.length > 0) {
                    filename = `${yearlyString}${selectedCompanyName}_all_projects_${selectedUsersNames}_${timestamp}.xlsx`;
                } else {
                    filename = `${yearlyString}${selectedCompanyName}_all_projects_and_users_${timestamp}.xlsx`;
                }
            }

            const response = await OneDrive.uploadFileToOneDrive(serversideToken, folderName, filename, data);
            const fullPath = response.webUrl;
            const simplePath = response.parentReference.path.split(":")[1];
            console.log(fullPath);
            console.log(filename);
            const popup = (
                <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2 text-green-600 dark:text-green-400 font-semibold text-lg">
                        <span className="text-2xl">✓</span>
                        <span>Report generated successfully!</span>
                    </div>
                    <div className="flex flex-col gap-2 text-sm">
                        <div>
                            <span className="font-medium text-(--colorNeutralForeground2)">File:</span>{" "}
                            <span className="font-mono text-(--colorNeutralForeground1)">{filename}</span>
                        </div>
                        <div>
                            <span className="font-medium text-(--colorNeutralForeground2)">Location:</span>{" "}
                            <span className="text-(--colorNeutralForeground1)">{simplePath}</span>
                        </div>
                        <div className="pt-2">
                            <Button
                                appearance="primary"
                                as="a"
                                href={fullPath}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full sm:w-auto"
                            >
                                Open in OneDrive
                            </Button>
                        </div>
                    </div>
                </div>
            );
            return popup;
        } catch (error) {
            console.error();
            return error;
        }
    };

    const getReport = async () => {
        setIsGeneratingReport(true);
        setPopup(
            <div className="flex items-center gap-2">
                <Spinner size="tiny" /> Generating report...
            </div>,
        );
        try {
            const isAllUsers = selectedUsersIDs.length === 0;

            const body: IFetchHoursBody = {
                company: selectedCompany,
                projects: selectedProjects,
                getAll: isAllUsers,
                users: isAllUsers ? [] : selectedUsersIDs,
                excludeDefaultProject,
            };

            // Handle date logic:
            // - If only start date: from startDate to today
            // - If only end date: from beginning to endDate
            // - If both dates: use both
            // - If neither: don't send dates (all data)
            if (startDate && endDate) {
                // Both dates provided
                body.from = startDate.valueOf();
                body.to = endDate.valueOf();
            } else if (startDate && !endDate) {
                // Only start date: from startDate to today (end of day)
                body.from = startDate.valueOf();
                const today = new Date();
                today.setHours(23, 59, 59, 999);
                body.to = today.getTime();
            } else if (!startDate && endDate) {
                // Only end date: from beginning to endDate
                body.to = endDate.valueOf();
                // from is undefined, which means all data from the beginning
            }
            // If neither date is provided, both from and to remain undefined (all data)

            let resp: BodyInit | undefined;

            if (token && serverSideToken) {
                switch (reportType) {
                    case ReportType.TIME_ENTRIES:
                        {
                            resp = await fetchHours(token, body); //Creates time entries report in backend (returs stream)
                        }
                        break;

                    case ReportType.YEAR_REPORT:
                        {
                            /** Get firt and last date of the current year */
                            const firstDay = new Date(year, 0, 1);
                            const lastDay = new Date(year, 11, 31);

                            body.from = firstDay.valueOf();
                            body.to = lastDay.valueOf();

                            resp = await fetchYearly(token, body);
                        }
                        break;

                    default:
                        break;
                }
                if (resp) {
                    const respPopup = await createReportWorkBook(serverSideToken, resp, reportType);
                    setPopup(respPopup);
                } else {
                    throw "No response from server!";
                }
            } else {
                throw "Token missing!";
            }
        } catch (error: any) {
            console.error(error);
            setPopup(<div className="text-red-600 font-bold">{JSON.stringify(error.error)}</div>);
        } finally {
            setIsGeneratingReport(false);
        }
    };

    const getCompanies = async (token: string) => {
        let companyDisabled = false;
        // setReady(false);

        const res = await fetchHasHours(token);

        /**Check that companies is not empty */
        if (res.companies === undefined || res.companies.length == 0) {
            console.warn("No companies found");
            companyDisabled = true;
            setCompanySelected(false);
        } else {
            companyDisabled = false;
        }
        // sort companies (alphabetically order take account lower and upper case)
        res.companies.sort((a: string, b: string) => a.localeCompare(b, "en", { sensitivity: "base" }));

        // Add special virtual options for "all companies" and "all Offcode users"
        const enhancedCompanies: string[] = ["== ALL ==", ...res.companies];
        setCompaniesList(enhancedCompanies);
        setProjectCompanyID(res.projectCompanyID);
        setIsCompanyDisabled(companyDisabled);
        // setReady(true);
    };

    const getCompanyID = (companyName: string) => {
        if (!token) {
            setErrorMsg("Token missing!");
            return;
        }

        // getCompanyByName returns the actual company from database with real ID
        getCompanyByName(token, companyName)
            .then((company) => {
                // Use the actual database ID from the company object
                setSelectedCompany(company.id);

                // Fetch projects for this company using the actual database ID
                return getProjects(token, company.id);
            })
            .then((projects) => {
                const projectNames: string[] = [];
                projects.forEach((project) => {
                    projectNames.push(project.project_name);
                });

                // Sort projects alphabetically (case-insensitive) for the dropdown
                projectNames.sort((a: string, b: string) => a.localeCompare(b, "en", { sensitivity: "base" }));
                setProjectsList(projectNames);
                setCurrentCompanyProjects(projects); // Store full project objects for the selected company
                setIsDisabled(false);
            })
            .catch((err) => {
                console.error(err);
                setErrorMsg("Error loading projects");
            });
    };

    const handleCompanyChange = (d: any) => {
        if (d.value && d.value !== "") {
            setSelectedCompanyName(d.value);

            if (d.value !== "== ALL ==") {
                // Specific company selected: load its projects
                getCompanyID(d.value);
                setCompanySelected(true);
            } else {
                // "ALL" selected: no specific company or project filter
                setSelectedCompany(undefined);
                setCompanySelected(true);
                setProjectsList([]);
                setCurrentCompanyProjects([]);
                setSelectedProjects([]);
                setSelectedProjectsName([]);
                setIsDisabled(true);
            }
        } else {
            // Clear company selection completely
            setSelectedCompanyName(undefined);
            setSelectedCompany(undefined);
            setCompanySelected(false);
            clearFields();
        }
    };

    const handleProjectChange = (e: any, d: any = []) => {
        const projectIDs: string[] = [];
        const projectNames: string[] = [];

        // Handle "All projects" selection (empty string)
        if (d.value === "" || d.value === null || d.value === undefined) {
            setSelectedProjectsName([]);
            setSelectedProjects([]);
            return;
        }

        // Handle both string (single selection) and array (multiple selection)
        const selectedValues = Array.isArray(d.value) ? d.value : [d.value];

        // Use currentCompanyProjects instead of projectCompanyID to ensure we only match
        // projects from the selected company
        selectedValues.forEach((projectName: string) => {
            if (projectName && projectName.trim() !== "") {
                const project = currentCompanyProjects.find((p) => p.project_name === projectName);
                if (project && project.id) {
                    projectIDs.push(project.id);
                    projectNames.push(projectName);
                }
            }
        });

        setSelectedProjectsName(projectNames);
        setSelectedProjects(projectIDs);
    };

    const handleUserChange = (e: any, d: any = []) => {
        const uIDArr: string[] = [];
        const userName = d.value || "";

        if (userName) {
            for (const user in usersData) {
                if (usersData[user].full_name === userName) {
                    uIDArr.push(usersData[user].id);
                    break;
                }
            }
        }

        // Store as string for single selection, but keep array type for compatibility
        setSelectedUsersNames(userName ? [userName] : []);
        setSelectedUsersIDs(uIDArr);
    };

    const clearFields = () => {
        setPopup(null);
        setSelectedCompany("");
        setSelectedCompanyName("");
        setSelectedProjects([]);
        setSelectedProjectsName([]);
        setSelectedUsersIDs([]);
        setSelectedUsersNames([]);
        setCompanySelected(false);
        setIsDisabled(true);
        setExcludeDefaultProject(false);
        setStartDate(undefined);
        setEndDate(undefined);
        setCurrentCompanyProjects([]); // Clear projects when clearing fields
    };

    const handleYearChange = (year: number) => {
        console.log(`year: ${year}`);
        setYear(year);
    };
    // Show loader while waiting for data or fetching
    if (isLoading || !token || !user || !context) {
        return <CustomLoader msg="Fetching data..." />;
    }

    return (
        <>
            {!errorMsg ? (
                <div className="p-2 sm:p-4 flex flex-col justify-start items-start min-h-full w-full max-w-full overflow-auto box-border bg-(--theme-bg-1)">
                    {companiesList ? (
                        <div className="w-full max-w-7xl mx-auto flex flex-col gap-4 p-4 sm:p-6 text-(--colorNeutralForeground1)">
                            {/* Header */}
                            <div className="w-full">
                                <h2 className="text-2xl sm:text-3xl font-semibold text-(--colorNeutralForeground1) mb-1">
                                    Download Reports
                                </h2>
                                <p className="text-sm text-(--colorNeutralForeground2)">Generate and download time tracking reports</p>
                            </div>

                            {/* Form */}
                            <div className="w-full bg-(--colorNeutralBackground1) rounded-lg border border-(--colorNeutralStroke1) p-4 sm:p-6 shadow-sm">
                                <div className="flex flex-col gap-4 sm:gap-5">
                                    {/* Main Form Fields */}
                                    <div className="flex flex-col gap-4 sm:gap-5">
                                        {/* Report Type */}
                                        <Field label="Report Type" required>
                                            <Select
                                                value={reportType || ""}
                                                onChange={(e, data) => {
                                                    if (data.value) {
                                                        setReportType(data.value as ReportType);
                                                        clearFields();
                                                    } else {
                                                        setReportType(undefined);
                                                        clearFields();
                                                    }
                                                }}
                                                className="w-full"
                                            >
                                                <option value="" disabled>
                                                    Select report type...
                                                </option>
                                                {REPORT_TYPES.map((type) => (
                                                    <option key={type} value={type}>
                                                        {type}
                                                    </option>
                                                ))}
                                            </Select>
                                        </Field>

                                        {reportType && (
                                            <>
                                                {/* Company Selection */}
                                                <Field label="Company" required>
                                                    <Select
                                                        disabled={isCompanyDisabled}
                                                        value={selectedCompanyName || ""}
                                                        onChange={(e, data) => {
                                                            if (data.value) {
                                                                handleCompanyChange({ value: data.value });
                                                            } else {
                                                                setSelectedCompanyName(undefined);
                                                                setSelectedCompany(undefined);
                                                                setCompanySelected(false);
                                                                clearFields();
                                                            }
                                                        }}
                                                        className="w-full"
                                                    >
                                                        <option value="" disabled>
                                                            Select company...
                                                        </option>
                                                        {companiesList?.map((company) => (
                                                            <option key={company} value={company}>
                                                                {company}
                                                            </option>
                                                        ))}
                                                    </Select>
                                                </Field>

                                                {reportType === ReportType.YEAR_REPORT ? (
                                                    /* Year Selection for Yearly Report */
                                                    <Field label="Year" required>
                                                        <YearDropdown projectChangeParentHandler={handleYearChange} />
                                                    </Field>
                                                ) : (
                                                    <>
                                                        {/* Project Selection */}
                                                        {companySelected && (
                                                            <Field label="Projects (optional - leave empty for all)">
                                                                <Select
                                                                    disabled={isDisabled}
                                                                    value={
                                                                        Array.isArray(selectedProjectsName)
                                                                            ? selectedProjectsName.join(",")
                                                                            : selectedProjectsName || ""
                                                                    }
                                                                    onChange={(e, data) => {
                                                                        handleProjectChange(e, data);
                                                                    }}
                                                                    className="w-full"
                                                                >
                                                                    <option value="">All projects</option>
                                                                    {projectsList?.map((project, index) => (
                                                                        <option key={`${project}-${index}`} value={project}>
                                                                            {project}
                                                                        </option>
                                                                    ))}
                                                                </Select>
                                                            </Field>
                                                        )}

                                                        {/* Date Range */}
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                            <Field label="Start Date (optional)">
                                                                <Input
                                                                    type="date"
                                                                    placeholder="Select start date"
                                                                    disabled={!companySelected}
                                                                    value={startDate ? moment(startDate).utc().format("YYYY-MM-DD") : ""}
                                                                    onChange={(e) => {
                                                                        if (e.target.value) {
                                                                            setStartDate(new Date(e.target.value).getTime());
                                                                        } else {
                                                                            setStartDate(undefined);
                                                                        }
                                                                    }}
                                                                    className="w-full date-input-calendar"
                                                                />
                                                            </Field>
                                                            <Field label="End Date (optional)">
                                                                <Input
                                                                    type="date"
                                                                    placeholder="Select end date"
                                                                    disabled={!companySelected}
                                                                    value={endDate ? moment(endDate).utc().format("YYYY-MM-DD") : ""}
                                                                    onChange={(e) => {
                                                                        if (e.target.value) {
                                                                            const date = new Date(e.target.value);
                                                                            setEndDate(date.getTime() + (23 * 60 + 59) * 60 * 1000);
                                                                        } else {
                                                                            setEndDate(undefined);
                                                                        }
                                                                    }}
                                                                    className="w-full date-input-calendar"
                                                                />
                                                            </Field>
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {/* Right Column - User Selection (only when applicable) */}
                                    {reportType && user && (user.is_admin || user.is_project_manager) && companySelected && (
                                        <div className="flex flex-col gap-2 sm:gap-3">
                                            <Field label="User (optional - leave empty for all users)">
                                                <Select
                                                    disabled={!companySelected}
                                                    value={
                                                        Array.isArray(selectedUsersNames) && selectedUsersNames.length > 0
                                                            ? selectedUsersNames[0]
                                                            : ""
                                                    }
                                                    onChange={(e, data) => {
                                                        handleUserChange(e, data);
                                                    }}
                                                    className="w-full"
                                                >
                                                    <option value="">All users</option>
                                                    {userList.map((userName) => (
                                                        <option key={userName} value={userName}>
                                                            {userName}
                                                        </option>
                                                    ))}
                                                </Select>
                                            </Field>

                                            <Field>
                                                <Checkbox
                                                    label='Exclude "Offcode - Default Hours" project'
                                                    checked={excludeDefaultProject}
                                                    onChange={(_, data) => setExcludeDefaultProject(!!data?.checked)}
                                                />
                                            </Field>
                                        </div>
                                    )}

                                    {/* Download Button - Full width at bottom */}
                                    {reportType && (
                                        <div
                                            className={`flex flex-col sm:flex-row gap-3 pt-2 ${reportType && user && (user.is_admin || user.is_project_manager) && companySelected ? "lg:col-span-2" : ""}`}
                                        >
                                            <Button
                                                appearance="primary"
                                                disabled={
                                                    isGeneratingReport ||
                                                    !companySelected ||
                                                    !reportType
                                                }
                                                onClick={getReport}
                                                className="w-full sm:w-auto min-w-[140px]"
                                            >
                                                {isGeneratingReport ? (
                                                    <span className="flex items-center gap-2">
                                                        <Spinner size="tiny" /> Generating...
                                                    </span>
                                                ) : (
                                                    "Download Report"
                                                )}
                                            </Button>
                                            {(selectedCompanyName || selectedProjectsName.length > 0) && (
                                                <Button
                                                    appearance="secondary"
                                                    onClick={clearFields}
                                                    disabled={isGeneratingReport}
                                                    className="w-full sm:w-auto"
                                                >
                                                    Clear
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Result/Status Message */}
                            {popup && (
                                <div className="w-full bg-(--colorNeutralBackground1) rounded-lg border border-(--colorNeutralStroke1) p-4 sm:p-6 shadow-sm">
                                    {popup}
                                </div>
                            )}

                            {/* Footer with version */}
                            <div className="w-full flex justify-end">
                                <div className="text-xs text-(--colorNeutralForeground3)">Version: {versionNumber}</div>
                            </div>
                        </div>
                    ) : null}
                </div>
            ) : (
                <ErrorMessage errorMsg={errorMsg} />
            )}
        </>
    );
}
